import React, {useRef, useState, useCallback} from 'react';

import {Button, useWindowSize} from '@newsela/angelou';
import {
  actionNames,
  actionPrefixes,
  eventTypes,
  objectTypes,
  useTracker,
  withTrackEvent,
} from '@newsela/snitch';
import PropTypes from 'prop-types';

import AISearchWithSparkleIcon from 'static/three-oh/src/components/GlobalLayout/GlobalLayoutV2/NavigationBar/AISearchMenuContainer/AISearchWithSparkleIcon/AISearchWithSparkleIcon.jsx';
import {NAVBAR_BREAKPOINTS} from 'static/three-oh/src/components/GlobalLayout/GlobalLayoutV2/NavigationBar/shared/constants';

import AIExpandableTextArea from './AIExpandableTextArea';
import {
  buttonWithPopOutRootCss,
  buttonIconContainerCss,
  buttonIconBorderCss,
  searchToggleButtonSvgCss,
  popOutCss,
} from './AISearchMenuContainer.styles';

const ButtonWithTrackEvent = withTrackEvent(Button);

const AISearchMenuContainer = ({
  onToggle,
  isAISearchEnabled,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const textAreaRef = useRef(null);
  const containerRef = useRef(null);

  const {width: windowWidth} = useWindowSize();
  const isMobileDisplay = windowWidth < NAVBAR_BREAKPOINTS.sm;

  const {trackAction, getEventName} = useTracker();

  const handleContainerBlur = (e) => {
    // Ensures that tabbing into elements within container
    // doesn't collapse the container. Only collapses when
    // focus is set outside of container.
    if (!containerRef.current?.contains(e.relatedTarget)) {
      setExpanded(false);
    }
  };

  const handleExpandAndFocus = () => {
    setExpanded(true);

    trackAction({
      event_type: eventTypes.click,
      event_name: getEventName(
        objectTypes.user,
        actionPrefixes.initiate,
        actionNames.interact,
      ),
      dimension_properties: {
        description: 'Toggle open the Search with Luna input.',
      },
    });
  };

  const handleToggleSearch = () => {
    setExpanded(!expanded);
  };

  const handleToggle = useCallback(() => {
    const newValue = !isAISearchEnabled;
    onToggle(newValue);
  }, [isAISearchEnabled, onToggle]);

  return (
    <>
      {isMobileDisplay ? (
        <li>
          <ButtonWithTrackEvent
            flavor={Button.flavors.incognito}
            size={Button.sizes.sm}
            onClick={handleToggleSearch}
            onBlur={handleContainerBlur}
            __cssFor={{
              root: buttonWithPopOutRootCss,
            }}
            objectType={objectTypes.user}
            actionName={actionNames.interact}
            streamProperties={{
              description: 'Toggle Search with Luna search bar on mobile.',
            }}
            dataProps={{
              'data-qa-selector': 'search-with-luna-mobile-toggle-button',
            }}
          >
            <div className={buttonIconContainerCss}>
              <div className={buttonIconBorderCss}>
                <AISearchWithSparkleIcon __cssFor={{root: searchToggleButtonSvgCss}} />
              </div>
            </div>
          </ButtonWithTrackEvent>
          {expanded ? (
            <div className={popOutCss}>
              <AIExpandableTextArea
                containerRef={containerRef}
                expanded={expanded}
                handleContainerBlur={handleContainerBlur}
                handleExpandAndFocus={handleExpandAndFocus}
                inputValue={inputValue}
                isMobileDisplay
                setExpanded={setExpanded}
                setInputValue={setInputValue}
                textAreaRef={textAreaRef}
                onToggle={handleToggle}
                pressed={isAISearchEnabled}
              />
            </div>
          ) : null}
        </li>
      ) : (
        <li>
          <AIExpandableTextArea
            containerRef={containerRef}
            expanded={expanded}
            handleExpandAndFocus={handleExpandAndFocus}
            handleContainerBlur={handleContainerBlur}
            inputValue={inputValue}
            setExpanded={setExpanded}
            setInputValue={setInputValue}
            textAreaRef={textAreaRef}
            onToggle={handleToggle}
            pressed={isAISearchEnabled}
          />
        </li>
      )}
    </>
  );
};

AISearchMenuContainer.propTypes = {
  onToggle: PropTypes.func,
  isAISearchEnabled: PropTypes.bool,
};

export default AISearchMenuContainer;
